import { Check } from 'lucide-react'

import type { LibraryItem } from '@/api/types'
import { Cover } from '@/components/Cover'
import { cn } from '@/lib/cn'
import { formatMs } from '@/lib/format'

export interface SelectableTrackRowProps {
  item: LibraryItem
  selected: boolean
  /** Shift means "extend from the last row I touched". */
  onToggle: (shiftKey: boolean) => void
}

function statusNote(item: LibraryItem): { text: string; tone: string } | null {
  switch (item.status) {
    case 'pending':
      return { text: 'Queued', tone: 'text-text-muted' }
    case 'downloading':
      return { text: 'Downloading', tone: 'text-accent' }
    case 'failed':
      return { text: 'Download failed', tone: 'text-danger' }
    case 'missing':
      return { text: 'File missing', tone: 'text-danger' }
    default:
      return null
  }
}

/**
 * The row as it looks while a list is being multi-selected.
 *
 * A separate presentation rather than a checkbox bolted onto `TrackRow`: in
 * selection mode the whole row is one control, which keeps the touch target at
 * full width, keeps the announced name honest ("select", not "play"), and
 * leaves no play, favourite or menu buttons to nest inside it. Same `h-16` as
 * the normal row, so switching modes shifts nothing.
 */
export function SelectableTrackRow({ item, selected, onToggle }: SelectableTrackRowProps) {
  const note = statusNote(item)
  const subtitle = item.track.album_name
    ? `${item.track.artist_name} · ${item.track.album_name}`
    : item.track.artist_name

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={(event) => onToggle(event.shiftKey)}
      className={cn(
        'flex h-16 w-full select-none items-center gap-3 rounded-lg px-2 text-left transition-colors',
        selected ? 'bg-accent-soft' : 'hover:bg-surface-hover',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
          selected ? 'border-accent bg-accent text-on-accent' : 'border-border-strong',
        )}
      >
        {selected ? <Check className="h-3.5 w-3.5" /> : null}
      </span>

      <Cover src={item.cover_url} size="sm" />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn('truncate text-sm font-medium', selected ? 'text-accent' : 'text-text')}>
          {item.track.title}
        </span>
        <span className="truncate text-sm text-text-muted">
          {note ? <span className={note.tone}>{note.text} · </span> : null}
          {subtitle}
        </span>
      </span>

      {item.track.duration_ms > 0 ? (
        <span className="shrink-0 px-1 text-sm tabular-nums text-text-subtle">
          {formatMs(item.track.duration_ms)}
        </span>
      ) : null}
    </button>
  )
}
